import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Power, PowerOff, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import PageShell from "@/components/masters/PageShell";
import { useAuth } from "@/context/AuthContext";
import {
  Field,
  GhostButton,
  OutlineButton,
  PrimaryButton,
  SearchBox,
  StatusBadge,
  inputCls,
  selectCls,
} from "@/components/masters/UI";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ROLES = ["Administrator", "Location Admin", "Supervisor", "Assistant Supervisor"];
const empty = { username: "", password: "", location_id: "", role: "Supervisor", status: "active" };

const UsersPage = () => {
  const { user: me, selectedLocationId } = useAuth();
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [q, setQ] = useState("");
  const [locFilter, setLocFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (locFilter) params.location_id = locFilter;
      const [usersRes, locsRes] = await Promise.all([
        api.get("/users", { params }),
        api.get("/locations", { params: { only_active: true } }),
      ]);
      setRows(usersRes.data);
      setLocations(locsRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, locFilter, selectedLocationId]);

  useEffect(() => { load(); }, [load]);

  const locName = useMemo(() => {
    const m = new Map(locations.map((l) => [l.id, l.name]));
    return (id) => m.get(id) || "—";
  }, [locations]);

  const startNew = () => {
    setEditing(null);
    setForm({ ...empty, location_id: locations[0]?.id || "" });
    setErr(null);
    setShowPwd(false);
    setOpen(true);
  };
  const startEdit = (row) => {
    setEditing(row);
    setForm({
      username: row.username,
      password: "",
      location_id: row.location_id,
      role: row.role,
      status: row.status,
    });
    setErr(null);
    setShowPwd(false);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.username.trim()) return setErr("Username is required.");
    if (!form.location_id) return setErr("Location is required.");
    if (!ROLES.includes(form.role)) return setErr("Please pick a valid role.");
    if (!editing && (!form.password || form.password.length < 6))
      return setErr("Password must be at least 6 characters.");
    if (editing && form.password && form.password.length < 6)
      return setErr("New password must be at least 6 characters.");

    setSaving(true);
    try {
      if (editing) {
        const body = { ...form };
        if (!body.password) delete body.password;
        await api.put(`/users/${editing.id}`, body);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    const next = row.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/users/${row.id}/status`, { status: next });
      toast.success(next === "active" ? "Activated" : "Inactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const noLocations = locations.length === 0;

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="users-page">
      <AppHeader />
      <PageShell
        eyebrow="Masters · Users"
        title="Console Users"
        subtitle="Who can sign into Vision. Each user has one Location and one role."
        primaryAction={
          <PrimaryButton testId="user-add" onClick={startNew} disabled={noLocations}>
            <Plus className="mr-1 inline h-4 w-4" /> Add User
          </PrimaryButton>
        }
        filters={
          <>
            <SearchBox value={q} onChange={setQ} placeholder="Search username…" testId="user-search" />
            <select
              className={selectCls + " w-56"}
              data-testid="user-filter-location"
              value={locFilter}
              onChange={(e) => setLocFilter(e.target.value)}
            >
              <option value="">All locations</option>
              {locations.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
            </select>
          </>
        }
        testId="users-shell"
      >
        <div
          className="overflow-hidden rounded-2xl border bg-white shadow-card"
          style={{ borderColor: "rgba(52,73,94,0.08)" }}
        >
          <div className="hidden grid-cols-[1.6fr_1.4fr_1.2fr_120px_160px] gap-4 bg-[color:var(--vision-ice)] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60 md:grid">
            <div>Username</div>
            <div>Role</div>
            <div>Location</div>
            <div>Status</div>
            <div className="text-right">Actions</div>
          </div>

          {loading && <div className="px-5 py-10 text-center text-sm text-vision-slate/60" data-testid="user-loading">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="px-5 py-14 text-center text-sm text-vision-slate/60" data-testid="user-empty">
              No users found.
            </div>
          )}

          {!loading && rows.map((row) => {
            const isMe = row.id === me?.id;
            return (
              <div
                key={row.id}
                data-testid={`user-row-${row.id}`}
                className="grid grid-cols-1 items-center gap-3 border-t px-5 py-4 md:grid-cols-[1.6fr_1.4fr_1.2fr_120px_160px] md:gap-4"
                style={{ borderColor: "rgba(52,73,94,0.08)" }}
              >
                <div className="flex items-center gap-2">
                  <span className="font-display text-sm font-bold text-vision-ink">{row.username}</span>
                  {isMe && (
                    <span className="chip"
                          style={{ backgroundColor: "rgba(0, 53, 158, 0.08)", color: "var(--vision-navy)", border: "1px solid rgba(0,53,158,0.20)" }}>
                      you
                    </span>
                  )}
                </div>
                <div className="text-sm text-vision-slate">{row.role}</div>
                <div className="text-sm text-vision-slate">{locName(row.location_id)}</div>
                <div><StatusBadge active={row.status === "active"} /></div>
                <div className="flex items-center justify-end gap-2">
                  <GhostButton testId={`user-edit-${row.id}`} onClick={() => startEdit(row)}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </GhostButton>
                  <GhostButton testId={`user-toggle-${row.id}`}
                               onClick={() => toggleStatus(row)}
                               disabled={isMe}
                               title={isMe ? "You can't deactivate yourself" : ""}>
                    {row.status === "active"
                      ? (<><PowerOff className="h-3.5 w-3.5" /> Inactivate</>)
                      : (<><Power className="h-3.5 w-3.5" /> Activate</>)}
                  </GhostButton>
                </div>
              </div>
            );
          })}
        </div>
      </PageShell>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-vision-ink">
              {editing ? "Edit User" : "Add User"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={submit} className="mt-2 grid gap-4">
            <Field label="Username">
              <input className={inputCls} data-testid="user-form-username"
                     value={form.username}
                     onChange={(e) => setForm({ ...form, username: e.target.value })}
                     placeholder="e.g. supervisor1" />
            </Field>

            <Field label={editing ? "New password" : "Password"}
                   hint={editing ? "Leave blank to keep the current password." : "At least 6 characters."}>
              <div className="relative">
                <input className={inputCls + " pr-11"} type={showPwd ? "text" : "password"}
                       autoComplete="new-password"
                       data-testid="user-form-password"
                       value={form.password}
                       onChange={(e) => setForm({ ...form, password: e.target.value })}
                       placeholder={editing ? "•••••••• (unchanged)" : "••••••••"} />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  data-testid="user-form-password-toggle"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                  title={showPwd ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-vision-slate/60 transition hover:bg-[color:var(--vision-ice)] hover:text-vision-slate"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Location">
                <select className={selectCls} data-testid="user-form-location"
                        value={form.location_id}
                        onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
                  <option value="">Select a location…</option>
                  {locations.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
                </select>
              </Field>
              <Field label="Role">
                <select className={selectCls} data-testid="user-form-role"
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  {ROLES.map((r) => (<option key={r} value={r}>{r}</option>))}
                </select>
              </Field>
            </div>

            <Field label="Status">
              <select className={selectCls} data-testid="user-form-status"
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>

            {err && (
              <div data-testid="user-form-error"
                   className="rounded-xl border px-4 py-3 text-sm"
                   style={{
                     backgroundColor: "rgba(231, 76, 60, 0.08)",
                     borderColor: "rgba(231, 76, 60, 0.3)",
                     color: "var(--vision-error)",
                   }}>{err}</div>
            )}

            <DialogFooter className="mt-2 gap-2 sm:justify-end">
              <OutlineButton type="button" testId="user-form-cancel" onClick={() => setOpen(false)}>Cancel</OutlineButton>
              <PrimaryButton type="submit" testId="user-form-submit" disabled={saving}>
                {saving ? "Saving…" : editing ? "Save changes" : "Create"}
              </PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
